import { useMemo } from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";
import { ArrowSquareOut } from "@phosphor-icons/react";
import ReadOnlyDocumentViewer from "../Document/ReadOnlyDocumentViewer";
import AIPaneMessagesTab from "../AIPane/MessagesTab";
import {
  ConversationOnlyPane,
  ConversationPane,
  DocumentPane,
  ModalBody,
  ModalMeta,
  ModalSubject,
  ModalTitleRow,
  ModalTitleText,
  OpenInMatterButton,
} from "./MessageThreadModalStyled";

const personName = (user) =>
  user?.name || user?.full_name || user?.username || user?.email || "Unknown teammate";

/**
 * "Open like an email" view for a Home-page message thread.
 *
 * Clicking a conversation on the Home Messages tab used to leave the
 * page entirely - a full navigate to the matter workspace just to read
 * one message. This renders the conversation (and its document, if the
 * thread has one) in place, the way an email client opens a message
 * without leaving the inbox. "Open in matter workspace" is kept as an
 * escape hatch for anyone who wants the full tools around the document.
 */
const MessageThreadModal = ({ open, thread, onClose, onOpenInMatter }) => {
  const threadDocument = thread?.document;
  const hasDocument = Boolean(threadDocument?.id);

  const subject = threadDocument?.name || thread?.document_name || "Matter conversation";

  const metaParts = useMemo(() => {
    const parts = [];
    const matterLabel = thread?.matter_name || thread?.matter_id;
    if (matterLabel) parts.push(`Matter: ${matterLabel}`);
    const participants = (thread?.participants || []).map(personName);
    if (participants.length) parts.push(`With: ${participants.join(", ")}`);
    return parts;
  }, [thread]);

  const title = (
    <ModalTitleRow>
      <ModalTitleText>
        <ModalSubject>{subject}</ModalSubject>
        {metaParts.length > 0 && <ModalMeta>{metaParts.join(" · ")}</ModalMeta>}
      </ModalTitleText>
      {onOpenInMatter && thread && (
        <OpenInMatterButton type="button" onClick={() => onOpenInMatter(thread)}>
          <ArrowSquareOut size={14} weight="bold" />
          Open in matter workspace
        </OpenInMatterButton>
      )}
    </ModalTitleRow>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      footer={null}
      width={hasDocument ? "min(1400px, 94vw)" : "min(760px, 94vw)"}
      destroyOnClose
      styles={{
        body: { height: "calc(100vh - 190px)", padding: 0, overflow: "hidden" },
      }}
      data-testid="message-thread-modal"
    >
      <ModalBody>
        {hasDocument && (
          <DocumentPane>
            <ReadOnlyDocumentViewer
              documentId={threadDocument.id}
              filename={threadDocument.name}
              fileType={threadDocument.file_type}
            />
          </DocumentPane>
        )}
        {hasDocument ? (
          <ConversationPane>
            <AIPaneMessagesTab
              documentId={threadDocument.id}
              isActive={open}
              threadId={thread?.id}
            />
          </ConversationPane>
        ) : (
          <ConversationOnlyPane>
            <AIPaneMessagesTab
              documentId={threadDocument?.id}
              isActive={open}
              threadId={thread?.id}
            />
          </ConversationOnlyPane>
        )}
      </ModalBody>
    </Modal>
  );
};

MessageThreadModal.propTypes = {
  open: PropTypes.bool,
  thread: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    matter_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    matter_name: PropTypes.string,
    document_name: PropTypes.string,
    participants: PropTypes.array,
    document: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      file_type: PropTypes.string,
      relative_path: PropTypes.string,
    }),
  }),
  onClose: PropTypes.func.isRequired,
  onOpenInMatter: PropTypes.func,
};

export default MessageThreadModal;
